import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import Collapse from '@material-ui/core/Collapse';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';

import InputControl from '~/components/Input/InputControl';
import InputSelect from '~/components/Input/InputSelect';
import GridContainer from '~/components/GridContainer';
import Dialog from '~/components/Dialog';
import Button from '~/components/Button';
import GridItem from '~/components/GridItem';
import LinearProgress from '~/components/LinearProgress';

import Request from '~/providers/Request';
import isBlank from '~/utils/isBlank';

import { packageOptions } from '~/config/constants';

function PackageList({ isOpen, user, owner, setIsOpen, setPackages }) {
  const { t } = useTranslation();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [specialties, setSpecialties] = useState([]);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [showSpecialtyOptions, setShowSpecialtyOptions] = useState(false);

  const [packPackages, setPackPackages] = useState([]);
  const [packsOptions, setPacksOptions] = useState([]);

  const onSave = () => {
    const packages = {};

    if (packs && packs.length > 0) {
      packs
        .filter((key) => key.id !== 'doctor_all')
        .map((pack) => {
          packages[pack.id] = true;
          return pack;
        });
    } else if (packs && packs.id) {
      packages[packs.id] = true;
    }

    let specialtiesIds = [];
    if (specialties && specialties.length > 0) {
      specialtiesIds = specialties.map((specialty) => {
        return specialty.id;
      });
    } else if (specialties && specialties.id) {
      specialtiesIds = [specialties.id];
    }

    if (showSpecialtyOptions && isBlank(specialtiesIds)) {
      toast.warning(t('person.extras.select_specialty'));
      return;
    }

    packages['specialties'] = specialtiesIds;

    let packPackagesIds = [];
    if (packPackages && packPackages.length > 0) {
      packPackagesIds = packPackages.map((packPackage) => {
        return packPackage.id;
      });
    } else if (packPackages && packPackages.id) {
      packPackagesIds = [packPackages.id];
    }

    packages['packPackages'] = packPackagesIds;

    const data = {
      owner_id: owner?.id || null,
      packs: packages,
    };

    setLoading(true);

    Request.post(`/admin/users/${user.id}/packages`, data, null, {
      normalizeJSON: false,
    })
      .then((response) => {
        setLoading(false);
        setPacks([]);
        setSpecialties([]);
        setPackages(response);
        toast.success(t('person.extras.activePackageSuccess'));
      })
      .catch((error) => {
        console.error(error);
        toast.success(t('person.extras.activePackageError'));
        toast.error(error.message);
        setLoading(false);
      });
  };

  const fetchSpecialties = () => {
    setLoading(true);
    Request.get('/admin/specialties', null, { normalizeJSON: false })
      .then((resp) => {
        const items = resp.data
          .filter((e) => e.attributes && e.attributes.is_active === true)
          .map((e) => ({
            id: Number(e.id),
            name: `${e.attributes.name}`,
          }));
        setSpecialtyOptions(items);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        console.error(e);
      });
  };

  async function fetchPacks() {
    Request.get('/admin/packs', null, { normalizeJSON: true })
      .then((response) => {
        setPacksOptions(response.data);
      })
      .catch((e) => {
        console.log(e);
        setPacksOptions([]);
      });
  }

  useEffect(() => {
    fetchSpecialties();
    fetchPacks();
  }, []);

  useEffect(() => {
    let hasDoctorAll = false;
    if (packs && packs.length > 0) {
      hasDoctorAll = packs.filter((key) => key.id === 'doctor_all').length > 0;
    }
    setShowSpecialtyOptions(hasDoctorAll);
    if (!hasDoctorAll) {
      setSpecialties([]);
    }
  }, [packs]);

  return (
    <>
      <Dialog
        icon={<PlaylistAddIcon />}
        isOpen={isOpen}
        handleClose={() => {
          setIsOpen(false);
        }}
        title={t('person.extras.newPack')}
        dialogActions={
          <React.Fragment>
            <Button onClick={onSave} disabled={loading} color="primary">
              {t('action.save')}
            </Button>
          </React.Fragment>
        }
      >
        <div style={{ minWidth: 512 }}>
          <LinearProgress loading={loading} />
          <GridContainer>
            <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
              <InputControl
                required
                name={'packs'}
                label={t('person.attributes.pack')}
                value={packs}
                inputComponent={InputSelect}
                onChange={({ packs }) => {
                  setPacks(packs);
                }}
                InputProps={{
                  isClearable: true,
                  isMulti: true,
                  options: Object.keys(packageOptions)
                    .filter((key) => key !== 'pack')
                    .map((key) => ({ id: key, name: packageOptions[key] })),
                }}
              />
            </GridItem>
          </GridContainer>

          <Collapse in={showSpecialtyOptions}>
            <GridContainer>
              <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <InputControl
                  name={'specialties'}
                  label={t('person.attributes.specialties')}
                  value={specialties}
                  inputComponent={InputSelect}
                  onChange={({ specialties }) => {
                    setSpecialties(specialties);
                  }}
                  InputProps={{
                    isClearable: true,
                    isMulti: true,
                    options: specialtyOptions,
                  }}
                />
              </GridItem>
            </GridContainer>
          </Collapse>

          <GridContainer>
            <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
              <InputControl
                name={'packPackages'}
                label={t('person.extras.pack_packages')}
                value={packPackages}
                inputComponent={InputSelect}
                onChange={({ packPackages }) => {
                  setPackPackages(packPackages);
                }}
                InputProps={{
                  isClearable: true,
                  isMulti: true,
                  options: packsOptions,
                }}
              />
            </GridItem>
          </GridContainer>

          <LinearProgress loading={loading} />
        </div>
      </Dialog>
    </>
  );
}

PackageList.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  setPackages: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  owner: PropTypes.object.isRequired,
};

export default React.memo(PackageList);
